import {
  AsyncThunk,
  AsyncThunkPayloadCreator,
  configureStore,
} from "@reduxjs/toolkit";
import AuthSlice from "./root/auth/infrastructure/slices/authSlice/authSlice";
import CarMutateSlice from "./root/car/infrastructure/slices/carMutateSlice";
import { createGQLClient } from "./shared/gql-client";
import CrudMutateSlice from "./root/general/infrastructure/slices/crudMutateSlice/crudMutateSlice";
import CrudTableSlice from "./root/general/infrastructure/slices/crudTableSlice/crudTableSlice";
import RequestMutateSlice from "./root/request/infrastructure/slices/requestMutateSlice";
import { RowData } from "@tanstack/react-table";
import bulkSelectSlice from "./root/general/infrastructure/slices/bulkSelectSlice/bulkSelectSlice";
import TireSetMutateSlice from "./root/tireset/infrastructure/slices/tireSetMutateSlice";
import settingsMutateSlice from "./root/planning/settings/infrastructure/slices/settingsMutateSlice";
import PlanningSlice from "./root/planning/dailyPlanning/infrastructure/redux/PlanningSlice";
import eventMutateSlice from "./root/event/infrastructure/eventMutateSlice";
import PackingListSlice from "./root/planning/packingList/infrastructure/redux/PackingListSlice";
import InvoiceDataSlice from "./root/invoice/infrastructure/redux/InvoiceDataSlice";
import { Row } from "@tanstack/table-core/build/lib/types";
import RequestQuickAddSlice from "./root/request/infrastructure/slices/requestQuickAddSlice";

const store = configureStore({
  reducer: {
    auth: AuthSlice,
    carMutate: CarMutateSlice,
    tiresetMutate: TireSetMutateSlice,
    requestMutate: RequestMutateSlice,
    requestQuickAdd: RequestQuickAddSlice,
    crudMutate: CrudMutateSlice,
    crudTable: CrudTableSlice,
    bulkSelected: bulkSelectSlice,
    planning: PlanningSlice,
    packingList: PackingListSlice,
    planningSettings: settingsMutateSlice,
    eventMutate: eventMutateSlice,
    invoiceData: InvoiceDataSlice,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: {
        extraArgument: {
          client: createGQLClient(),
        },
      },
      serializableCheck: false,
    }),
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;

export default store;

declare module "@reduxjs/toolkit" {
  type AsyncThunkConfig = {
    state?: unknown;
    dispatch?: AppDispatch;
    extra?: unknown;
    rejectValue?: unknown;
    serializedErrorType?: unknown;
  };

  function createAsyncThunk<
    Returned,
    ThunkArg = void,
    ThunkApiConfig extends AsyncThunkConfig = {
      state: RootState;
    },
  >(
    typePrefix: string,
    payloadCreator: AsyncThunkPayloadCreator<
      Returned,
      ThunkArg,
      ThunkApiConfig
    >,
    options?: unknown,
  ): AsyncThunk<Returned, ThunkArg, ThunkApiConfig>;
}

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    columnAction?: string | "disabled";
    updateData?: (row: Row<TData>, value: unknown) => void;
  }
}
